// Tier identity + allotments for the Tour Dates Automation Package. Dollar
// prices derive from these allotments in ./automations-pricing
// (docs/specs/credits-pricing-approach.md). A Mogul plan is ops-created in Stripe:
// metadata purchase_category=automations, artist_id, tier=mogul, interval=monthly, credit_allotment=N.

/** Ascending by allotment — pricing lookups rely on this order. */
export const AUTOMATIONS_TIERS = [
  "starter",
  "pro",
  "feature",
  "headliner",
] as const;
export type AutomationsTier = (typeof AUTOMATIONS_TIERS)[number];

export const AUTOMATIONS_BILLING_INTERVALS = ["monthly", "annual"] as const;
export type AutomationsBillingInterval =
  (typeof AUTOMATIONS_BILLING_INTERVALS)[number];

/** Monthly credit allotment per tier — the ONE home for these numbers. */
export const AUTOMATIONS_TIER_CREDITS: Record<AutomationsTier, number> = {
  starter: 10_000,
  pro: 30_000,
  feature: 50_000,
  headliner: 100_000,
};

export function isAutomationsTier(value: unknown): value is AutomationsTier {
  return (
    typeof value === "string" &&
    (AUTOMATIONS_TIERS as readonly string[]).includes(value)
  );
}

export const AUTOMATIONS_MOGUL_TIER = "mogul";

export function isMogulPlan(
  snapshot: { tier: string | null } | null | undefined
): boolean {
  return snapshot?.tier === AUTOMATIONS_MOGUL_TIER;
}

export function isAutomationsBillingInterval(
  value: unknown
): value is AutomationsBillingInterval {
  return (
    typeof value === "string" &&
    (AUTOMATIONS_BILLING_INTERVALS as readonly string[]).includes(value)
  );
}

export interface AutomationsPlanKey {
  tier: AutomationsTier;
  interval: AutomationsBillingInterval;
}

export function isSamePlan(a: AutomationsPlanKey, b: AutomationsPlanKey) {
  return a.tier === b.tier && a.interval === b.interval;
}

/** A plan as one comparable string, e.g. for a dependency or a sync key. */
export function automationsPlanKeyOf({ tier, interval }: AutomationsPlanKey) {
  return `${tier}|${interval}`;
}

export type AutomationsPlanChange = "same" | "now" | "scheduled";

/**
 * How a plan change lands: a higher tier on either cycle, or the same tier
 * going monthly to annual, takes effect now (prorated); everything else waits
 * for the renewal. The one rule the modal and the route share.
 */
export function planChange(
  current: AutomationsPlanKey,
  target: AutomationsPlanKey
): AutomationsPlanChange {
  if (isSamePlan(current, target)) return "same";
  const tierDelta =
    AUTOMATIONS_TIERS.indexOf(target.tier) -
    AUTOMATIONS_TIERS.indexOf(current.tier);
  const switchesToAnnual =
    tierDelta === 0 &&
    current.interval === "monthly" &&
    target.interval === "annual";
  return tierDelta > 0 || switchesToAnnual ? "now" : "scheduled";
}
